import React, { useEffect, useState } from 'react';
import { useTranslations } from 'hooks/useTranslations';
import { Grid } from '@mui/material';
import { useConfigurationLicence } from '../hooks';
import { Accordion } from 'components/molecules/Accordion';
import { EButtonVariants } from 'constants/Buttons';
import { EIconTypes } from 'constants/Icons';
import Button from 'components/atoms/Button';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { clearAlertTimeout, EAlertTime } from 'utils/date';
import { useParams } from 'react-router-dom';
import { EPerPages, ISortOptions } from 'models/table';
import { Loader } from 'components/atoms/Loader';
import { useThemeBreakpoint } from 'hooks/useThemeBreakpoint';
import { Table } from 'components/molecules/Table';
import { EDictionaryTypes } from 'models/dictionary';
import { useLang } from 'models/langContext';
import { Pagination } from 'components/molecules/Pagination';
import {
  ApplicationProcessDocumentTableColumns,
  mapApplicationProcessDocument,
} from '../helper';
import { CardList } from 'components/molecules/CardList';
import { LicenceTemplateApplicationProcessAddDocument } from '../LicenceTemplateApplicationProcessAddDocument';
import ApplicationProcessDocumentTableCard from '../ApplicationProcessDocumentTableCard';
import { FormModal } from 'components/molecules/Modal';
import { EPermission } from 'models/permissions';

const LicenceTemplateApplicationProcessDocument = () => {
  const { t } = useTranslations();
  const {
    handleGetApplicationProccessDocument,
    applicationProccessDocuments,
    handleDeleteApplicationProccessDocument,
    paginatorApplicationProccessDocuments,
    loadDictionaries,
    attachmentDocumentTypeData,
  } = useConfigurationLicence();
  const { isDesktop } = useThemeBreakpoint();
  const { selectedLanguage } = useLang();
  const { processId = '' } = useParams<{ processId: string }>();
  const { clearAlert, documentAlert } = useAlert();
  const [isAddDocumentModal, setIsAddDocumentModal] = useState(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<EPerPages>(EPerPages.perPage25);
  const [sort, setSort] = useState<ISortOptions | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [applicationProcessDocumentDelete, setApplicationProcessDocumentDelete] = useState<
    string | null
  >();

  useEffect(() => {
    (async function initDictionaries() {
      await loadDictionaries([EDictionaryTypes.attachmentDocumentTypeData]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLanguage]);

  useEffect(() => {
    (async () => {
      setIsLoading(true);
      await handleGetApplicationProccessDocument(processId, currentPage, perPage, sort);
      setIsLoading(false);
    })();
  }, [currentPage, perPage, sort]);

  useEffect(
    () => () => {
      clearAlertTimeout(clearAlert, ActionTypes.CLEAR_TRANSACTION_ALERT, EAlertTime.LONG);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const reloadList = async () => {
    await handleGetApplicationProccessDocument(processId, currentPage, perPage, sort);
  };

  return (
    <>
      {isAddDocumentModal && (
        <LicenceTemplateApplicationProcessAddDocument
          addSuccess={reloadList}
          handleClose={() => setIsAddDocumentModal(false)}
          id={processId}
        />
      )}
      <Accordion
        title={t('licence.documents.label')}
        name="documents"
        key="price"
        alwaysExpanded
        hideExpandIcon
      >
        <Grid container sx={{ justifyContent: 'flex-end', alignItems: 'center', mb: 2 }}>
          <Grid sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Button
              label={t('licence.addDocument.label')}
              id="addDocuments"
              variant={EButtonVariants.contained}
              onClick={() => setIsAddDocumentModal(true)}
              icon={EIconTypes.plus}
              size="medium"
              fullWidth
              permission={EPermission.MANAGE_WEAPON_LICENCE_CONFIGURATOR}
              sx={{ minWidth: { sm: '120px' } }}
            />
          </Grid>
        </Grid>
        <FormModal
          open={!!applicationProcessDocumentDelete}
          onClickClose={() => {
            setApplicationProcessDocumentDelete(null);
          }}
          onClickApprove={() => {
            if (applicationProcessDocumentDelete) {
              handleDeleteApplicationProccessDocument(applicationProcessDocumentDelete).then(
                () => reloadList(),
              );
              setApplicationProcessDocumentDelete(null);
            }
          }}
          title={t('licenceConfigurator.document.delete.text')}
          approveButtonLabel="general.yes.label"
          cancelButtonLabel="general.no.label"
        />
        {documentAlert && (
          <Grid sx={{ mb: 2 }}>
            <Grid size={{ xs: 12 }}>
              <Alert text={documentAlert.text} variant={documentAlert.variant} />
            </Grid>
          </Grid>
        )}
        {isLoading ? (
          <Loader isLoading={true} />
        ) : (
          <>
            {isDesktop ? (
              <Table
                columns={ApplicationProcessDocumentTableColumns({
                  handleDelete: (id) => {
                    setApplicationProcessDocumentDelete(id);
                  },
                })}
                dataSource={mapApplicationProcessDocument(
                  applicationProccessDocuments,
                  attachmentDocumentTypeData,
                )}
                onSort={(it) => {
                  setSort(it);
                }}
              />
            ) : (
              <CardList
                items={mapApplicationProcessDocument(
                  applicationProccessDocuments,
                  attachmentDocumentTypeData,
                )}
                render={(data, index) => (
                  <ApplicationProcessDocumentTableCard
                    data={data}
                    key={index}
                    handleDelete={(id: string) => setApplicationProcessDocumentDelete(id)}
                  />
                )}
              />
            )}

            {paginatorApplicationProccessDocuments && (
              <Pagination
                count={paginatorApplicationProccessDocuments?.totalElements}
                onChangePage={(page) => setCurrentPage(page)}
                current={currentPage}
                perPage={perPage}
                onChangePerPage={(value) => setPerPage(value)}
                isLoading={isLoading}
              />
            )}
          </>
        )}
      </Accordion>
    </>
  );
};

export { LicenceTemplateApplicationProcessDocument };
